import axios, { AxiosError, type AxiosInstance } from "axios";
import router from "@/router";
import { useContextStore } from "@/stores/context";
import { useRateLimitStore } from "@/stores/rateLimit";
import { useNotifications } from "@/components/useNotifications";
import { invalidateCachePattern, clearAllCache } from "@/helpers/apiCache";

const baseURL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:3000';

// Custom Error Class for specific Catch blocks in UI components
export class RateLimitError extends Error {
  constructor(public retryAfter: number, message: string) {
    super(message);
    this.name = "RateLimitError";
  }
}

// --- Axios Configuration ---
axios.defaults.withCredentials = true;
axios.defaults.baseURL = baseURL;

// Protect Legacy Axios calls with Interceptors
axios.interceptors.request.use((config) => {
  const rateLimitStore = useRateLimitStore();
  if (rateLimitStore.checkIsStillRateLimited()) {
    return Promise.reject(new RateLimitError(0, "Rate limit active. Please wait."));
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 429) {
      handleRateLimitResponse(error.response.headers);
    }
    if (error.response?.status === 401) {
      await handleUnauthorized();
    }
    if (error.response?.status === 403) {
      await handleForbidden();
    }
    if (error.response?.status === 404) {
      const url = error.config?.url || "";
      await handleNotFound(url);
    }
    return Promise.reject(error);
  }
);

// Legacy Axios instance export
// explicitly typed to prevent 'unique symbol' TypeScript inference errors
export const HttpClient: Pick<AxiosInstance, 'get' | 'post' | 'patch' | 'put' | 'delete'> = {
  get: axios.get,
  post: axios.post,
  patch: axios.patch,
  put: axios.put,
  delete: axios.delete,
};

// --- Fetch Wrapper ---
export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const rateLimitStore = useRateLimitStore();

  if (rateLimitStore.checkIsStillRateLimited()) {
    throw new RateLimitError(0, "Rate limit active. Please wait.");
  }

  const mergedInit: RequestInit = { ...init, credentials: "include" };
  const response = await fetch(input, mergedInit);

  if (response.status === 429) {
    handleRateLimitResponse(response.headers);
  }

  const allow401 = (init as any)?.allow401 === true;
  if (response.status === 401 && !allow401) {
    await handleUnauthorized();
  }

  const allow403 = (init as any)?.allow403 === true;
  if (response.status === 403 && !allow403) {
    await handleForbidden();
  }

  const allow404 = (init as any)?.allow404 === true;
  if (response.status === 404 && !allow404) {
    await handleNotFound(response.url);
  }

  return response;
}

// --- Shared Handlers ---
function handleRateLimitResponse(headers: Headers | Record<string, any> = {}) {
  const rateLimitStore = useRateLimitStore();

  const getHeader = (name: string): string | null => {
    if (headers instanceof Headers) {
      return headers.get(name);
    }
    if (typeof headers.get === "function") {
      const val = headers.get(name.toLowerCase());
      return val ? String(val) : null;
    }
    const val = headers[name.toLowerCase()];
    return val ? String(val) : null;
  };

  const resetHeader = getHeader("ratelimit-reset") || getHeader("retry-after");
  const retryAfterSeconds = resetHeader ? parseInt(resetHeader, 10) : 60;

  rateLimitStore.triggerRateLimit(retryAfterSeconds);

  throw new RateLimitError(
    retryAfterSeconds,
    `Rate limited. Try again in ${retryAfterSeconds}s`
  );
}

async function handleUnauthorized() {
  const contextStore = useContextStore();
  contextStore.clearUser();
  contextStore.clearAllSelections();
  clearAllCache();
  localStorage.removeItem("context");

  const isAuthOrLandingRoute =
    router.currentRoute.value?.name === "landing" ||
    router.currentRoute.value?.name === "auth.login" ||
    window.location.pathname === "/" ||
    window.location.pathname === "/login";

  if (!isAuthOrLandingRoute) {
    const currentPath = window.location.pathname + window.location.search + window.location.hash;
    try {
      await router.replace({
        name: "auth.login",
        query: { redirect: currentPath, session: "expired" },
      });
    } catch {
      window.location.href = `/login?session=expired&redirect=${encodeURIComponent(currentPath)}`;
    }
  }
}

async function handleNotFound(requestUrl: string = "") {
  const contextStore = useContextStore();
  const route = router.currentRoute.value;
  const lowerUrl = requestUrl.toLowerCase();

  const isGuildResource = lowerUrl.includes("channels") || lowerUrl.includes("roles");
  const isPanelResource = lowerUrl.includes("panel");
  const isAppResource = lowerUrl.includes("application");

  // SCENARIO A: Bot Kicked
  if (isGuildResource) {
    try {
      const { addNotification } = useNotifications();
      addNotification({
        type: "warning",
        title: "Resource Unavailable",
        message: "The bot is missing or server is inaccessible. Returning to server list...",
        duration: 5000,
      });
    } catch (e) { }

    contextStore.clearPanelSelection();
    contextStore.clearApplicationSelection();
    contextStore.clearServerSelection();
    await router.replace({ name: "pages.servers", query: { forceLeave: "true" } });
    return;
  }

  // SCENARIO B: Panel / App Deleted by another admin
  if ((isPanelResource || isAppResource) && (route?.name === "pages.panel.edit" || route?.name === "pages.application.edit" || route?.name === "moderation.edit-template")) {
    const guildId = contextStore.currentServer?.id || (route.params.guildId as string);

    if (guildId) {
      if (isPanelResource) {
        invalidateCachePattern(`panels:${guildId}`);
        invalidateCachePattern(`panel:${guildId}`);
      }
      if (isAppResource) {
        invalidateCachePattern(`applications:${guildId}`);
        invalidateCachePattern(`app:${guildId}`);
      }
    }

    try {
      const { addNotification } = useNotifications();
      addNotification({
        type: "warning",
        title: isAppResource ? "Application Unavailable" : "Panel Unavailable",
        message: `This ${isAppResource ? 'application' : 'panel'} no longer exists. Returning to list...`,
        duration: 5000,
      });
    } catch (e) { }

    if (isAppResource) {
      contextStore.clearApplicationSelection();
    } else {
      contextStore.clearPanelSelection();
    }
    
    await router.replace({
      name: isAppResource ? "pages.applications" : "pages.panels",
      params: { guildId },
      query: { forceLeave: "true", refresh: Date.now().toString() }
    });
  }
}

async function handleForbidden() {
  const contextStore = useContextStore();
  contextStore.clearServerSelection();

  const currentRouteName = router.currentRoute.value?.name as string | undefined;
  const isGlobalRoute = currentRouteName === "pages.servers" || currentRouteName === "landing";

  if (!isGlobalRoute) {
    try {
      const { addNotification } = useNotifications();
      addNotification({
        type: "error",
        title: "Access Denied",
        message: "You have lost access to this server, or the bot was removed.",
        duration: 5000,
      });
    } catch (e) { }

    try {
      await router.replace({ name: "pages.servers", query: { session: "forbidden", forceLeave: "true" } });
    } catch {
      window.location.href = `/dashboard/servers?session=forbidden`;
    }
  }
}